/**
 * TreeMapOrganism
 *
 * Adapter that supplies the TreeMap visualization body to `ContextShellOrganism`.
 * All context selection, view resolution, session/personalization, filters,
 * shared links, the switcher, the banner and the view-editor overlay live in
 * the shell (`organisms/ContextShell/ContextShellOrganism`); this component
 * owns only what is genuinely TreeMap-specific: the fade-in transition on
 * context load, wiring the resolved config's card-view density into the
 * session + personalization layers, and the display pass-through props.
 */

import React, { useEffect, useState, useRef, type ReactNode } from 'react';
import ContextShellOrganism, {
  type ContextShellChrome,
} from '../ContextShell/ContextShellOrganism';
import TreeMap from '../TreeMap/TreeMap';
import type { TreeMapProps, CardView } from '../TreeMap/types';
import type { TreeMapViewConfig, BaseViewConfig } from 'types/contextView';

/**
 * `levelFilters`/`runtimeFilters`/`filterSaveTargets`/`onSetLevelFilter`/`canEditContext`/
 * `onEditContext`/`onShareWithView` are owned by `ContextShellOrganism` (via `useLevelFilters` +
 * `useContextPermissions`) and computed from `activeContextSystemName`/`view`
 * — a caller can't supply them directly. `linkSuppressed`/`onExitSharedLink`
 * are the same class of thing (shared-link state the shell now owns
 * unconditionally, EAINT-793 Phase 4) — omitted so a stray same-named prop
 * on the caller's side can't fall through `...otherProps` in `TreeMapBody`
 * below and silently override the shell-computed value in the JSX that sets
 * it explicitly.
 *
 * `onCardViewChange` and `onBoardStateChange` are omitted for that same reason
 * and must stay in step: both are set explicitly by the adapter *before* the
 * `{...otherProps}` spread, so a caller-supplied one would win. For
 * `onBoardStateChange` that would replace the "board interactions write to the
 * session layer *and* the persisted personalization overlay" pair with a
 * caller's handler, and resize/hide/collapse would quietly stop persisting.
 */
export interface TreeMapOrganismProps
  extends Omit<
    TreeMapProps,
    | 'context'
    | 'viewConfig'
    | 'baseConfig'
    | 'contextBar'
    | 'onCardViewChange'
    | 'onBoardStateChange'
    | 'levelFilters'
    | 'runtimeFilters'
    | 'filterSaveTargets'
    | 'onSetLevelFilter'
    | 'canEditContext'
    | 'onEditContext'
    | 'onShareWithView'
    | 'linkSuppressed'
    | 'onExitSharedLink'
  > {
  /**
   * Component default context system name.
   * Used when the user has no localStorage selection and no override.
   * Defaults to TREEMAP_DEFAULT_CONTEXT_SYSTEM_NAME when omitted.
   */
  contextSystemName?: string;
  /** Hard override — bypasses localStorage (e.g., a metaspace template that pins a context) */
  contextSystemNameOverride?: string;
  /** Organization ID for context lookup */
  organizationId: string;
  /** Root sphere ID to start the hierarchy from */
  rootSphereId: string;
  /**
   * Optional ContextView system name for view-specific config. Overridden by
   * a `?view=` query param, if present — the shell reads `?view=`/`?f=`
   * itself (EAINT-793 Phase 4), so shared-link support requires no wiring
   * here at all.
   */
  viewSystemName?: string;
  /** Inline TreeMapViewConfig overrides — merged into the `view` layer, below the session layer */
  viewConfig?: Partial<TreeMapViewConfig>;
  /** Inline BaseViewConfig overrides — merged into the `view` layer, below the session layer */
  baseConfig?: Partial<BaseViewConfig>;
  /** Whether the tree map is editable */
  editable?: boolean;
  /** Show breadcrumbs navigation */
  showBreadcrumbs?: boolean;
  /** Show filters */
  showFilters?: boolean;
  /** Show AI assistant */
  showAI?: boolean;
  /** Default page size for pagination */
  defaultPageSize?: number;
  /** Use grid layout instead of column layout */
  useGridLayout?: boolean;
  /** Initial card view mode */
  initialCardView?: 'minimal' | 'standard' | 'expanded';
  /** Callback when node is selected */
  onNodeSelect?: (nodeId: string) => void;
  /** Callback when node is created */
  onNodeCreate?: (node: unknown) => void;
  /** Callback when node is updated */
  onNodeUpdate?: (node: unknown) => void;
  /** Callback when node is deleted */
  onNodeDelete?: (nodeId: string) => void;
  /** Navigate to the "New context" creation form */
  onNewContext?: () => void;
  /** Navigate to the context management page */
  onManageContexts?: () => void;
  /**
   * Navigate to the context editor for the given context — the "Edit in context"
   * affordance (EAINT-781 § Widening) for relaxing a locked, context-authored
   * filter. Omitted entirely from `LevelFilterPopoverOrganism` when absent.
   */
  onEditContext?: (contextSystemName: string) => void;
  /**
   * Render prop for the inline context editor. When provided, "Open context"
   * from the view editor opens the context editor inline instead of navigating away.
   */
  renderContextEditor?: (params: {
    contextSystemName: string;
    onBack: () => void;
    onClose: () => void;
  }) => ReactNode;
}

const TreeMapOrganism: React.FC<TreeMapOrganismProps> = (props) => {
  const {
    contextSystemName,
    contextSystemNameOverride,
    organizationId,
    rootSphereId,
    viewSystemName,
    viewConfig: inlineViewConfig,
    baseConfig: inlineBaseConfig,
    editable = true,
    showBreadcrumbs = true,
    showFilters = true,
    showAI = true,
    defaultPageSize = 10,
    useGridLayout = true,
    initialCardView = 'standard',
    onNodeSelect,
    onNodeCreate,
    onNodeUpdate,
    onNodeDelete,
    onNewContext,
    onManageContexts,
    onEditContext,
    renderContextEditor,
    ...otherProps
  } = props;

  // Lives here, above the shell, because the shell unmounts `TreeMapBody` on
  // every loading gate — including a same-context view switch.
  const prevContextIdRef = useRef<string | null>(null);

  return (
    <ContextShellOrganism
      contextSystemName={contextSystemName}
      contextSystemNameOverride={contextSystemNameOverride}
      organizationId={organizationId}
      rootSphereId={rootSphereId}
      viewSystemName={viewSystemName}
      viewConfig={inlineViewConfig}
      baseConfig={inlineBaseConfig}
      onNewContext={onNewContext}
      onManageContexts={onManageContexts}
      onEditContext={onEditContext}
      renderContextEditor={renderContextEditor}
      // TreeMap owns a header slot for the bar (`TreeMap/types.ts`'s
      // `contextBar`) — declare it explicitly rather than relying on the
      // shell's 'above' default (D-1).
      chromePlacement="slot"
    >
      {(chrome) => (
        <TreeMapBody
          chrome={chrome}
          organizationId={organizationId}
          rootSphereId={rootSphereId}
          editable={editable}
          showBreadcrumbs={showBreadcrumbs}
          showFilters={showFilters}
          showAI={showAI}
          defaultPageSize={defaultPageSize}
          useGridLayout={useGridLayout}
          initialCardView={initialCardView}
          onNodeSelect={onNodeSelect}
          onNodeCreate={onNodeCreate}
          onNodeUpdate={onNodeUpdate}
          onNodeDelete={onNodeDelete}
          otherProps={otherProps}
          prevContextIdRef={prevContextIdRef}
        />
      )}
    </ContextShellOrganism>
  );
};

interface TreeMapBodyProps {
  chrome: ContextShellChrome;
  organizationId: string;
  rootSphereId: string;
  editable: boolean;
  showBreadcrumbs: boolean;
  showFilters: boolean;
  showAI: boolean;
  defaultPageSize: number;
  useGridLayout: boolean;
  initialCardView: CardView;
  onNodeSelect?: (nodeId: string) => void;
  onNodeCreate?: (node: unknown) => void;
  onNodeUpdate?: (node: unknown) => void;
  onNodeDelete?: (nodeId: string) => void;
  otherProps: Record<string, unknown>;
  /** Owned by `TreeMapOrganism` so it outlives this body's remounts — see the fade-in note below. */
  prevContextIdRef: React.MutableRefObject<string | null>;
}

/**
 * A real component (not an inline closure) so the fade-in transition below
 * can hold its own hooks — a render-prop callback that calls hooks directly
 * would attribute them to `ContextShellOrganism`'s fiber instead of its own.
 */
const TreeMapBody: React.FC<TreeMapBodyProps> = ({
  chrome,
  organizationId,
  rootSphereId,
  editable,
  showBreadcrumbs,
  showFilters,
  showAI,
  defaultPageSize,
  useGridLayout,
  initialCardView,
  onNodeSelect,
  onNodeCreate,
  onNodeUpdate,
  onNodeDelete,
  otherProps,
  prevContextIdRef,
}) => {
  const { body, contextBar } = chrome;

  // ── Fade-in animation on context load ─────────────────────────────────────
  // `contextEntering` is transient and may reset with this component; the
  // "which context did we last fade in" memory must NOT, which is why the ref
  // is owned by `TreeMapOrganism` and passed down. The shell unmounts this body
  // whenever it hits its loading gate, and `useContextView` reports `loading`
  // on any *view* switch too — so a ref declared here would reset on a
  // same-context view switch and replay the 300ms fade that never used to fire.
  const [contextEntering, setContextEntering] = useState(false);

  useEffect(() => {
    if (body.context && body.context.id !== prevContextIdRef.current) {
      prevContextIdRef.current = body.context.id;
      setContextEntering(true);
      const timer = setTimeout(() => setContextEntering(false), 300);
      return () => clearTimeout(timer);
    }
  }, [body.context, prevContextIdRef]);

  return (
    <TreeMap
      context={body.context}
      viewConfig={body.viewConfig}
      baseConfig={body.baseConfig}
      organizationId={organizationId}
      rootSphereId={rootSphereId}
      editable={editable}
      showBreadcrumbs={showBreadcrumbs}
      showFilters={showFilters}
      showAI={showAI}
      defaultPageSize={defaultPageSize}
      useGridLayout={useGridLayout}
      // Same precedence as resolveLevelConfig's density chain: the view's
      // own defaultCardView, then the seeded base_config.cardDensity, then
      // the caller's prop.
      initialCardView={body.viewConfig.defaultCardView ?? body.baseConfig.cardDensity ?? initialCardView}
      onNodeSelect={onNodeSelect}
      onNodeCreate={onNodeCreate}
      onNodeUpdate={onNodeUpdate}
      onNodeDelete={onNodeDelete}
      levelFilters={body.levelFilters}
      runtimeFilters={body.runtimeFilters}
      filterSaveTargets={body.filterSaveTargets}
      onSetLevelFilter={body.onSetLevelFilter}
      linkSuppressed={body.linkSuppressed}
      onExitSharedLink={body.onExitSharedLink}
      canEditContext={body.canEditContext}
      onEditContext={body.onEditContext}
      onShareWithView={body.onShareWithView}
      // Board interactions write to session (immediate render) and the
      // debounced, persisted personalization overlay — never the dirty
      // rail: a personal tweak must not become everyone's (spec § Writes
      // go to exactly one layer).
      onCardViewChange={(density) => {
        body.patchSessionView({ defaultCardView: density });
        body.patchPersonalizationView({ defaultCardView: density });
      }}
      onBoardStateChange={(patch) => {
        body.patchSessionView(patch);
        body.patchPersonalizationView(patch);
      }}
      className={contextEntering ? 'tree-map--context-entering' : ''}
      contextBar={contextBar}
      {...otherProps}
    />
  );
};

export default TreeMapOrganism;
